import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getEvent, getItems, submitRsvp, getHeadcount } from "@/lib/api";
import { WaveDivider } from "@/components/WaveDivider";
import { RsvpForm } from "@/components/RsvpForm";
import { Confirmation } from "@/components/Confirmation";
import { HeadcountBar } from "@/components/HeadcountBar";
import { Waves, Sun, Sparkles, MapPin, ArrowDown, UtensilsCrossed } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760926455212-d42b180ad602?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwcG9vbCUyMGZsb2F0JTIwc3Vuc2V0fGVufDB8fHx8MTc4NTUyMjU3MHww&ixlib=rb-4.1.0&q=85";
const SLIDE_IMG =
  "https://images.pexels.com/photos/4005314/pexels-photo-4005314.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FLOAT_IMG =
  "https://images.unsplash.com/photo-1565190503580-3fe10c3f12db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwcG9vbCUyMGZsb2F0JTIwc3Vuc2V0fGVufDB8fHx8MTc4NTUyMjU3MHww&ixlib=rb-4.1.0&q=85";

const PARTY_DATETIME = "Sat Aug 15, 2:00 PM until whenever on Sunday";

export default function PublicPage() {
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [headcount, setHeadcount] = useState({ total: 0, goal: 20 });
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadItems = async () => {
    try {
      setItems(await getItems());
    } catch (e) {
      /* ignore */
    }
  };

  const loadHeadcount = async () => {
    try {
      setHeadcount(await getHeadcount());
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setEvent(await getEvent());
        await Promise.all([loadItems(), loadHeadcount()]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const scrollToForm = () => {
    document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (payload) => {
    const res = await submitRsvp(payload);
    setConfirmation(res);
    loadHeadcount();
    loadItems();
    if (res?.items_missed?.length) {
      toast.warning(
        `Someone grabbed ${res.items_missed.join(", ")} first — the rest are all yours!`
      );
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isClosed = event?.rsvp_status === "closed";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-retro-cream">
        <div className="font-chango text-2xl text-retro-orange animate-pulse" data-testid="loading-indicator">
          Splashing in...
        </div>
      </div>
    );
  }

  if (confirmation) {
    return <Confirmation data={confirmation} floatImg={FLOAT_IMG} onDone={() => { setConfirmation(null); loadItems(); }} />;
  }

  return (
    <div className="relative z-[2] min-h-screen bg-retro-cream" data-testid="public-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Poolside sunset" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-retro-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-b from-retro-orange/30 via-transparent to-retro-pink/40" />
        </div>

        <div className="relative mx-auto flex min-h-[92vh] max-w-4xl flex-col items-start justify-center px-6 py-20 sm:px-10">
          <div className="mb-6 inline-flex -rotate-2 items-center gap-2 border-2 border-retro-ink bg-retro-yellow px-4 py-2 shadow-retro">
            <Sun className="h-5 w-5 text-retro-ink" />
            <span className="font-chango text-sm tracking-tight text-retro-ink">The First Annual!</span>
          </div>

          <h1 className="font-chango text-5xl leading-[0.95] tracking-tight text-white drop-shadow-[3px_3px_0px_#1A2A3A] sm:text-7xl lg:text-8xl" data-testid="hero-title">
            Slip-and-<span className="text-retro-yellow">Slide</span> Party
          </h1>

          <div className="mt-6 inline-flex rotate-1 items-center gap-2 border-2 border-retro-ink bg-white px-5 py-3 shadow-retro">
            <Waves className="h-5 w-5 text-retro-blue" />
            <span className="font-chango text-base tracking-tight text-retro-ink sm:text-lg" data-testid="hero-datetime">
              {event?.datetime || PARTY_DATETIME}
            </span>
          </div>

          <p className="mt-6 max-w-xl text-lg font-semibold text-white/95 sm:text-xl" data-testid="hero-tagline">
            Grab your swimsuit and your competitive streak, we've got lawn games, a slip-and-slide, and sips outside ☀️
          </p>

          <button
            onClick={scrollToForm}
            data-testid="hero-rsvp-button"
            className="group mt-9 inline-flex items-center gap-3 rounded-full border-2 border-retro-ink bg-retro-orange px-9 py-4 font-chango text-xl text-white shadow-retro-lg transition-[transform,box-shadow] duration-150 hover:translate-x-[3px] hover:translate-y-[3px] hover:bg-retro-orangeDark hover:shadow-retro-sm"
          >
            RSVP Now
            <ArrowDown className="h-5 w-5 transition-transform group-hover:translate-y-1" />
          </button>
        </div>

        <WaveDivider color="#FDF6ED" />
      </section>

      {/* DETAILS */}
      <section className="mx-auto max-w-4xl px-6 py-16 sm:px-10 sm:py-20" data-testid="details-section">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div className="animate-fade-up">
            <div className="mb-4 inline-flex -rotate-1 items-center gap-2 border-2 border-retro-ink bg-retro-pink px-4 py-1.5 shadow-retro-sm">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="font-chango text-sm text-white">The Lowdown</span>
            </div>
            <h2 className="font-chango text-3xl leading-tight text-retro-ink sm:text-4xl">
              What to bring?
            </h2>
            <ul className="mt-4 space-y-2" data-testid="what-to-bring-list">
              {["Sunscreen", "BYOB", "Bathing Suit", "and claim a group item"].map((li) => (
                <li key={li} className="flex items-center gap-3 text-lg font-semibold text-retro-ink">
                  <span className="inline-block h-3 w-3 shrink-0 rotate-45 border-2 border-retro-ink bg-retro-orange" aria-hidden="true" />
                  {li}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-base font-medium text-retro-ink/80">
              Overnight guests are welcome pitch a tent or claim a couch. Just don't forget your sleeping bag and a pillow.
            </p>

            <div className="mt-6 flex items-start gap-3 border-2 border-retro-ink bg-retro-yellow p-4 shadow-retro-sm" data-testid="food-note">
              <UtensilsCrossed className="mt-0.5 h-6 w-6 shrink-0 text-retro-ink" />
              <div>
                <p className="font-chango text-lg text-retro-ink">Food</p>
                <p className="text-sm font-semibold text-retro-ink/90">
                  Dinner on the 15th and breakfast on the 16th are on us. Everything else, see the bring list.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3 border-2 border-dashed border-retro-blue bg-retro-blue/10 p-4" data-testid="address-hidden-notice">
              <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-retro-blue" />
              <div>
                <p className="font-chango text-lg text-retro-ink">Address revealed when you RSVP.</p>
                <p className="text-sm font-medium text-retro-ink/70">Count yourself in below and we'll drop the pin.</p>
              </div>
            </div>
          </div>

          <div className="relative -rotate-2 animate-fade-up">
            <img
              src={SLIDE_IMG}
              alt="Water slide"
              className="w-full border-2 border-retro-ink object-cover shadow-retro-lg"
              style={{ aspectRatio: "4/5" }}
            />
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_slip-and-slide/artifacts/88twz2dt_ChatGPT%20Image%20Jul%2031%2C%202026%2C%2012_04_18%20PM.png"
              alt="Party sticker"
              data-testid="bobble-sticker"
              className="animate-bobble pointer-events-none absolute left-[20%] top-[5%] z-10 w-[42%] -translate-x-1/2 drop-shadow-[3px_5px_4px_rgba(0,0,0,0.35)]"
            />
            <div className="absolute -bottom-4 -right-3 z-20 rotate-6 border-2 border-retro-ink bg-retro-yellow px-4 py-2 font-chango text-retro-ink shadow-retro">
              Fun guaranteed
            </div>
          </div>
        </div>
      </section>

      <WaveDivider color="#4A90E2" />

      {/* RSVP */}
      <section id="rsvp" className="bg-retro-blue/90 px-6 py-16 sm:px-10 sm:py-20" data-testid="rsvp-section">
        <div className="mx-auto max-w-xl">
          <div className="mb-8 text-center">
            <h2 className="font-chango text-4xl text-white drop-shadow-[2px_2px_0px_#1A2A3A] sm:text-5xl">
              Count Me In
            </h2>
            <p className="mt-3 font-semibold text-white/90">
              Please RSVP with this form, and pick the group items you'll bring.
            </p>
          </div>

          <HeadcountBar total={headcount.total} goal={headcount.goal} />

          {isClosed ? (
            <div className="border-2 border-retro-ink bg-white p-8 text-center shadow-retro-lg" data-testid="rsvp-closed-notice">
              <div className="font-chango text-2xl text-retro-orange">RSVPs are closed.</div>
              <p className="mt-3 text-lg font-medium text-retro-ink/80">
                Text the Orsers if you still want in.
              </p>
            </div>
          ) : (
            <RsvpForm items={items} onReloadItems={loadItems} onSubmit={handleSubmit} />
          )}
        </div>
      </section>

      <footer className="bg-retro-ink py-8 text-center">
        <p className="font-chango text-lg text-retro-yellow">See you at the slide!</p>
        <a href="/admin" data-testid="admin-footer-link" className="mt-2 inline-block text-sm font-medium text-white/50 hover:text-white/90">
          Host access
        </a>
      </footer>
    </div>
  );
}
